import React, { useState } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Checkbox, 
    FormControlLabel,
    IconButton,
    Alert,
    Grid,
    Divider
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';
import { addLiquidityProvider } from '../../api/lpApi';

// Validation schema
const validationSchema = yup.object({
    name: yup
        .string()
        .required('LP name is required')
        .min(1, 'Name must be at least 1 character')
        .max(200, 'Name cannot exceed 200 characters'),
    isActive: yup.boolean(),
    priority: yup
        .number()
        .required('Priority is required')
        .min(1, 'Priority must be at least 1')
        .max(100, 'Priority cannot exceed 100')
        .integer('Priority must be a whole number'),
    lpType: yup
        .string()
        .max(50, 'LP type cannot exceed 50 characters')
        .nullable(),
    region: yup
        .string()
        .max(10, 'Region cannot exceed 10 characters')
        .nullable(),
    isEcn: yup.boolean(),
    creditLimit: yup
        .number()
        .min(0, 'Credit limit cannot be negative')
        .nullable(),
    notes: yup
        .string()
        .max(1000, 'Notes cannot exceed 1000 characters')
        .nullable()
});

const AddLPModal = ({ open, onClose, onSuccess }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Reset form when modal opens/closes
    React.useEffect(() => {
        if (open) {
            setError(null);
            setIsSubmitting(false);
        }
    }, [open]);

    const formik = useFormik({
        initialValues: {
            name: '',
            isActive: true,
            priority: 1,
            lpType: '',
            region: '',
            isEcn: false,
            creditLimit: null,
            notes: ''
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true);
            setError(null);

            try {
                // Get token from localStorage
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                // Prepare data for API call
                const lpData = {
                    name: values.name,
                    isActive: values.isActive,
                    priority: values.priority,
                    lpType: values.lpType || null,
                    region: values.region || null,
                    isEcn: values.isEcn,
                    creditLimit: values.creditLimit || null,
                    notes: values.notes || null
                };

                console.log('Submitting LP Data:', lpData); // Debug log

                // Call API
                const result = await addLiquidityProvider(token, lpData);
                
                // Success
                toast.success(`Liquidity Provider "${values.name}" added successfully!`);
                formik.resetForm(); // Reset form after successful submission
                onSuccess && onSuccess(result);
                onClose();
                
            } catch (error) {
                console.error('Error adding LP:', error);
                setError(error.message || 'Failed to add liquidity provider');
                toast.error(error.message || 'Failed to add liquidity provider');
            } finally {
                setIsSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        if (!isSubmitting) {
            formik.resetForm();
            setError(null);
            onClose();
        }
    };

    const lpTypes = [
        'Prime Broker',
        'ECN Broker',
        'Exchange',
        'Market Maker',
        'STP Broker',
        'NDD Broker'
    ];

    const regions = [
        'US',
        'EU',
        'UK',
        'ASIA',
        'GLOBAL'
    ];

    return (
        <Modal
            open={open}
            onClose={handleClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Box sx={{
                width: '90vw',
                maxWidth: 800,
                maxHeight: '90vh',
                background: 'rgba(24, 31, 44, 0.95)',
                borderRadius: 2,
                border: '1px solid rgba(6, 182, 212, 0.3)',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AddIcon sx={{ color: '#1de9b6', fontSize: 24 }} />
                        <Typography variant="h5" sx={{ color: '#1de9b6', fontWeight: 700 }}>
                            Add New Liquidity Provider
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: '#fff' }} disabled={isSubmitting}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Info Banner */}
                <Alert 
                    severity="info" 
                    icon={<InfoIcon />}
                    sx={{
                        background: '#1976d2',
                        color: '#fff',
                        '& .MuiAlert-icon': { color: '#fff' },
                        borderRadius: 0
                    }}
                >
                    Configure a new liquidity provider to expand your trading capabilities
                </Alert>

                {/* Error Display */}
                {error && (
                    <Alert 
                        severity="error"
                        sx={{
                            background: '#d32f2f',
                            color: '#fff',
                            '& .MuiAlert-icon': { color: '#fff' },
                            borderRadius: 0
                        }}
                    >
                        {error}
                    </Alert>
                )}

                {/* Form */}
                <Box component="form" onSubmit={formik.handleSubmit} sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
                                Basic Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="LP Name *"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                                disabled={isSubmitting}
                                sx={{
                                    '& .MuiInputLabel-root': { color: '#bfc9db' },
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                        '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                        '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: '#bfc9db' }}>LP Type</InputLabel>
                                <Select
                                    name="lpType"
                                    value={formik.values.lpType}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={isSubmitting}
                                    sx={{
                                        color: '#fff',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(6, 182, 212, 0.3)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(6, 182, 212, 0.5)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1de9b6'
                                        }
                                    }}
                                >
                                    <MenuItem value="">Select LP Type</MenuItem>
                                    {lpTypes.map((type) => (
                                        <MenuItem key={type} value={type}>{type}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Priority *"
                                name="priority"
                                type="number"
                                value={formik.values.priority}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.priority && Boolean(formik.errors.priority)}
                                helperText={formik.touched.priority && formik.errors.priority}
                                disabled={isSubmitting}
                                inputProps={{ min: 1, max: 100 }}
                                sx={{
                                    '& .MuiInputLabel-root': { color: '#bfc9db' },
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                        '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                        '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel sx={{ color: '#bfc9db' }}>Region</InputLabel>
                                <Select
                                    name="region"
                                    value={formik.values.region}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    disabled={isSubmitting}
                                    sx={{
                                        color: '#fff',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(6, 182, 212, 0.3)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(6, 182, 212, 0.5)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1de9b6'
                                        }
                                    }}
                                >
                                    <MenuItem value="">Select Region</MenuItem>
                                    {regions.map((region) => (
                                        <MenuItem key={region} value={region}>{region}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Credit Limit"
                                name="creditLimit"
                                type="number"
                                value={formik.values.creditLimit || ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.creditLimit && Boolean(formik.errors.creditLimit)}
                                helperText={formik.touched.creditLimit && formik.errors.creditLimit}
                                disabled={isSubmitting}
                                inputProps={{ min: 0, step: 0.01 }}
                                sx={{
                                    '& .MuiInputLabel-root': { color: '#bfc9db' },
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                        '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                        '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                    }
                                }}
                            />
                        </Grid>

                        {/* Configuration Options */}
                        <Grid item xs={12}>
                            <Divider sx={{ borderColor: 'rgba(6, 182, 212, 0.2)', my: 2 }} />
                            <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
                                Configuration Options
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isActive"
                                        checked={formik.values.isActive}
                                        onChange={formik.handleChange}
                                        disabled={isSubmitting}
                                        sx={{
                                            color: '#1de9b6',
                                            '&.Mui-checked': { color: '#1de9b6' }
                                        }}
                                    />
                                }
                                label="Active"
                                sx={{ color: '#bfc9db' }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="isEcn"
                                        checked={formik.values.isEcn}
                                        onChange={formik.handleChange}
                                        disabled={isSubmitting}
                                        sx={{
                                            color: '#1de9b6',
                                            '&.Mui-checked': { color: '#1de9b6' }
                                        }}
                                    />
                                }
                                label="ECN (Electronic Communication Network)"
                                sx={{ color: '#bfc9db' }}
                            />
                        </Grid>

                        {/* Notes */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Notes"
                                name="notes"
                                multiline
                                rows={3}
                                value={formik.values.notes}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.notes && Boolean(formik.errors.notes)}
                                helperText={formik.touched.notes && formik.errors.notes}
                                disabled={isSubmitting}
                                placeholder="Additional notes about this liquidity provider..."
                                sx={{
                                    '& .MuiInputLabel-root': { color: '#bfc9db' },
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                        '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                        '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}>
                    <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={handleClose}
                        disabled={isSubmitting}
                        sx={{
                            color: '#ff6b6b',
                            borderColor: '#ff6b6b',
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={formik.handleSubmit}
                        disabled={isSubmitting || !formik.isValid}
                        sx={{
                            background: '#1de9b6',
                            color: '#000',
                            borderRadius: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#19b89b'
                            },
                            '&:disabled': {
                                background: 'rgba(29, 233, 182, 0.3)',
                                color: 'rgba(0, 0, 0, 0.3)'
                            }
                        }}
                    >
                        {isSubmitting ? 'Adding...' : 'Add LP'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddLPModal;
